import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { DISCHARGE_CALENDAR_ROUTES } from 'components/dischargeCalendar/constants';
import { TASKS_ROUTES } from 'components/inboundReferrals/tasks/constants';
import { Flags } from 'constants/flags';
import { useEnvironment } from 'context/environment';
import { useProfile } from 'context/profile';
import { useReferralTerminology } from 'hooks/useReferralTerminology';
import { ImportType } from 'models/Client';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';
import AdmissionsIcon from 'svg/AdmissionsIcon';
import AllPatientsIcon from 'svg/AllPatientsIcon';
import ArrowLeft from 'svg/ArrowLeft';
import BusinessDevelopmentIcon from 'svg/BusinessDevelopmentIcon';
import ContractedHealthPlansIcon from 'svg/ContractedHealthPlansIcon';
import CoordinatePatientsIcon from 'svg/CoordinatePatientsIcon';
import DischargeCalendarIcon from 'svg/DischargeCalendarIcon';
import GearIcon from 'svg/GearIcon';
import InboundReferralsIcon from 'svg/InboundReferralsIcon';
import OutboundReferralsIcon from 'svg/OutboundReferralsIcon';
import PatientPortfolioIcon from 'svg/PatientPortfolioIcon';
import PerformanceEngagementIcon from 'svg/PerformanceEngagementIcon';
import PillIcon from 'svg/PillIcon';
import ReferralCoordinationIcon from 'svg/ReferralCoordinationIcon';
import ReferralSourceIcon from 'svg/ReferralSourceIcon';
import SummarizationIcon from 'svg/SummarizationIcon';
import UserXIcon from 'svg/UserXIcon';

import { ADMIN_NAV_LINKS } from './admin/constants';
import AccessGuard from './shared/AccessGuard';
import SideNavLink from './SideNavLink';
import { SideNavSection } from './SideNavSection';

const SideNavbar = () => {
  const { profile } = useProfile();
  const { listLabel } = useReferralTerminology();
  const { environment } = useEnvironment();
  const location = useLocation();
  const isAdminPageActive = profile.isAdmin && location.pathname.startsWith('/admin');
  const [isAdminView, setIsAdminView] = useState(isAdminPageActive);

  // Demo Env Setup is the only admin tool that writes demo data — server-side
  // Demo::EnvironmentGuard refuses to run the seeding worker in production.
  // Hide the nav link in production so the feature isn't discoverable there,
  // but keep it visible in every other env (staging, demo, sandbox, loadtest,
  // local dev) so the team can exercise it during QA + use it for training
  // setup. Production is identified by DATADOG_ENVIRONMENT === 'production'
  // (the only env JSON that sets it to that exact value; staging is 'staging',
  // demo/sandbox are empty, loadtest is 'loadtest', local dev is undefined).
  const isProductionEnvironment = environment?.DATADOG_ENVIRONMENT === 'production';

  const showConnectPatients = (() => {
    return (
      profile.has(PERMISSIONS.patientCreate) &&
      profile.actingClient.importType === ImportType.IMPORTED_PATIENT &&
      profile.canActAsManager &&
      (profile.isAdmin || (!profile.isPhysicianTeamUser && !profile.isAffiliateUser))
    );
  })();
  const showCoordinateRequest =
    profile.actingClient.providedProviderTypes.length > 0 && profile.has(PERMISSIONS.reviewCoordinateRequestCreate);
  const showCoordinate = !profile.isSnfCustomerUser && (showConnectPatients || showCoordinateRequest);

  // Care Coordination product access, read directly rather than through <AccessGuard>: the
  // merged "Referrals" section spans both sides of that guard (OLIO-2905), so the items that
  // used to sit under "Transitions of Care" carry the check on themselves instead.
  const hasCareCoordination = profile.has(PRODUCT_ACCESSORS.careCoordinationEnabled);
  const showReferrals = profile.actingClient.handlesReferrals;
  const showAdmissions =
    profile.has(PRODUCT_ACCESSORS.admissionsManagementEnabled) && profile.has(PERMISSIONS.reviewAdmissionView);
  const showDenialList =
    profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) &&
    profile.has(PERMISSIONS.denialListView) &&
    profile.has(PERMISSIONS.denialListItemView);
  const showHighCostMeds =
    profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) &&
    profile.has(PERMISSIONS.highCostMedicationListView) &&
    profile.has(PERMISSIONS.highCostMedicationListItemView);
  // Product access AND the per-user Flipper flag, matching the Referrals section itself rather than
  // the other two Configuration pages. Those are product-access-only, and copying them put this
  // page in the nav for a client who has Inbound Referrals product access but not the flag — a
  // page for a feature they cannot otherwise see. Pre-beta the flag is what decides who has the
  // feature at all, so it decides this too.
  //
  // The ADMIN entry deliberately does not check it: Olio staff set a customer's list up before the
  // flag reaches them, which is the reason that entry exists.
  const showReferralSources =
    profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) &&
    profile.hasFlag(Flags.InboundReferrals) &&
    profile.has(PERMISSIONS.referralSourceView);
  const hasHealthPlans = profile.actingClient.providedProviderTypes.some((x) => x.config.designatesAcceptedPlans);
  // Product access plus the per-user Flipper flag (P1-1). The flag is on the user (enabled for
  // internal dev/qa users). Insights still rides this alone: it reads aggregates from its own
  // endpoint, not the referral payload, so referral view is not what gates it.
  const showInboundReferrals =
    profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) && profile.hasFlag(Flags.InboundReferrals);
  // The queues themselves need view as well (OLIO-2615) — every page under this section 403s
  // without it, so showing the section would only offer a dead end.
  const showReferralQueues = showInboundReferrals && profile.has(PERMISSIONS.workflowInboundReferralView);
  // Tasks is offered by configuration, not by product: a client working every portal by hand has
  // no automated work for the page to hold.
  const showTasks = showReferralQueues && profile.has(PRODUCT_ACCESSORS.connectedPortalsEnabled);
  const showSnfCustomerInsights = hasCareCoordination && profile.isSnfCustomerUser;

  if (isAdminView) {
    return (
      <NavContainer>
        <AdminNavHeader onClick={() => setIsAdminView(false)}>
          <ArrowLeft />
          <span>Viewing: Admin</span>
        </AdminNavHeader>
        <AdminNavDivider />
        <AdminNavContent>
          {ADMIN_NAV_LINKS.map((link) => (
            <AccessGuard key={link.title} accessor={link.accessor} strictMode={link.strictMode}>
              {link.children ? (
                <SideNavLink
                  title={link.title}
                  to={link.children.map((child) => ({
                    subtitle: child.title,
                    to: child.to,
                    permission: child.accessor,
                  }))}
                />
              ) : (
                <SideNavLink to={link.to ?? ''} title={link.title} end={link.end} />
              )}
            </AccessGuard>
          ))}
          {profile.isAdmin && !isProductionEnvironment && (
            <SideNavLink to='/admin/demo-env-setup' title='Demo Env Setup' />
          )}
        </AdminNavContent>
      </NavContainer>
    );
  }

  return (
    <NavContainer>
      <NavContent>
        <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
          <SideNavSection title='Care Coordination'>
            <SideNavLink
              to='/patients'
              title='Patient Portfolio'
              data-cy='patientPortfolioLink'
              icon={<PatientPortfolioIcon width={16} height={16} />}
            />
            <SideNavLink
              to='/all-patients'
              title='All Patients'
              data-cy='allPatientsLink'
              icon={<AllPatientsIcon width={16} height={16} />}
            />
            <SideNavLink
              to='/coordinate'
              data-cy='coordinateLink'
              title='Coordinate'
              icon={<CoordinatePatientsIcon color={'currentColor'} width={16} height={16} />}
              visible={showCoordinate}
            />
          </SideNavSection>
          <SideNavSection title='Census Management' visible={profile.has(PRODUCT_ACCESSORS.dischargeCalendarEnabled)}>
            <SideNavLink
              to={DISCHARGE_CALENDAR_ROUTES.BASE}
              title='Discharge Calendar'
              data-cy='discharge-calendar'
              icon={<DischargeCalendarIcon width={16} height={16} />}
              end={false}
            />
          </SideNavSection>
        </AccessGuard>
        {/*
          One top-level "Referrals" header replacing the old "Transitions of Care" +
          "Referrals" pair (OLIO-2905). It sits outside the Care Coordination guard because
          Inbound Referrals never needed that product access, so the two items that did —
          Admissions and the outbound list — check `hasCareCoordination` themselves. The
          section's own `visible` has to be the OR of all three: SideNavSection renders its
          title whenever visible, regardless of whether any child is.
        */}
        <SideNavSection
          title='Referrals'
          visible={showReferralQueues || (hasCareCoordination && (showReferrals || showAdmissions))}>
          <SideNavLink
            data-cy='inboundReferralsLink'
            title='Inbound Referrals'
            icon={<InboundReferralsIcon width={16} height={16} />}
            end={false}
            visible={showReferralQueues}
            // Tasks is a child of Inbound Referrals, not a sibling section — it's the same
            // work seen as an inbox. `matchNestedRoutesOnly` isn't needed here because an
            // expandable parent matches by route hierarchy already. Without Tasks there is
            // nothing to expand, so the item is a flat link like its siblings.
            to={
              showTasks
                ? [
                    { subtitle: 'Referrals', to: '/inbound-referrals' },
                    { subtitle: 'Tasks', to: TASKS_ROUTES.BASE },
                  ]
                : '/inbound-referrals'
            }
          />
          <SideNavLink
            data-cy='referrals-link'
            to='/referrals'
            title={listLabel}
            icon={<OutboundReferralsIcon width={16} height={16} />}
            visible={hasCareCoordination && showReferrals}
          />
          <SideNavLink
            data-cy='admissions-link'
            to='/admissions'
            title='Admissions'
            icon={<AdmissionsIcon width={16} height={16} />}
            visible={hasCareCoordination && showAdmissions}
            end={false}
          />
        </SideNavSection>
        <SideNavSection title='Insights' visible={hasCareCoordination || showInboundReferrals}>
          <SideNavLink
            visible={hasCareCoordination}
            title='Performance & Engagement'
            icon={<PerformanceEngagementIcon width={16} height={16} />}
            // Non-SNF P&E is a bare /insights link; match by route hierarchy so it doesn't also
            // highlight sibling insights sections like /insights/inbound-referrals.
            matchNestedRoutesOnly
            to={
              showSnfCustomerInsights
                ? [
                    { to: '/insights', subtitle: 'Facility Dashboard' },
                    { to: '/insights/post-discharge', subtitle: 'Post Discharge Dashboard' },
                  ]
                : '/insights'
            }
          />
          <SideNavLink
            visible={showSnfCustomerInsights || showInboundReferrals}
            title='Referral Coordination'
            icon={<ReferralCoordinationIcon width={16} height={16} />}
            to={
              showSnfCustomerInsights
                ? showInboundReferrals
                  ? [
                      { subtitle: 'Inbound Referrals', to: '/insights/inbound-referrals' },
                      { subtitle: 'Outbound Referrals', to: '/insights/referrals' },
                    ]
                  : '/insights/referrals'
                : '/insights/inbound-referrals'
            }
          />
          <SideNavLink
            visible={showSnfCustomerInsights}
            to='/insights/business-development'
            title='Business Development'
            icon={<BusinessDevelopmentIcon width={16} height={16} />}
          />
        </SideNavSection>
        <SideNavSection title='Workspaces' visible={profile.has(PRODUCT_ACCESSORS.olioSummarizationEnabled)}>
          <SideNavLink
            title='Summarization'
            data-cy='summarizationLink'
            icon={<SummarizationIcon width={16} height={16} />}
            to={[
              {
                to: '/summarizations/new',
                subtitle: 'New Summary',
              },
              {
                to: '/summarizations',
                subtitle: 'History',
              },
            ]}
          />
        </SideNavSection>
        <SideNavSection
          title='Configuration'
          visible={
            showDenialList || showHighCostMeds || showReferralSources || (hasCareCoordination && hasHealthPlans)
          }>
          {/* Moved here from "Transitions of Care"; gating is unchanged (OLIO-2905). */}
          <SideNavLink
            data-cy='contractedHealthPlansLink'
            to='/configuration/contracted-health-plans'
            title='Contracted Health Plans'
            icon={<ContractedHealthPlansIcon width={16} height={16} />}
            visible={hasCareCoordination && hasHealthPlans}
          />
          <SideNavLink
            to='/configuration/denial-list'
            title='Denial List'
            data-testid='denialListLink'
            icon={<UserXIcon width={16} height={16} />}
            visible={showDenialList}
          />
          <SideNavLink
            to='/configuration/high-cost-medications'
            title='High Cost Medications'
            data-testid='highCostMedsLink'
            icon={<PillIcon width={16} height={16} />}
            visible={showHighCostMeds}
          />
          <SideNavLink
            to='/configuration/referral-sources'
            title='Referral Sources'
            data-testid='referralSourcesLink'
            icon={<ReferralSourceIcon width={16} height={16} />}
            visible={showReferralSources}
          />
        </SideNavSection>
        <SideNavSection title='Admin' visible={profile.isAdmin}>
          <AdminNavButton $active={isAdminPageActive} onClick={() => setIsAdminView(true)}>
            <GearIcon width={18} height={18} />
            <span className='link-title'>Admin</span>
          </AdminNavButton>
        </SideNavSection>
      </NavContent>
    </NavContainer>
  );
};

export default SideNavbar;

const NavContainer = styled.div`
  height: 100%;
  min-width: ${({ theme }) => theme.dimensions.sideNavWidth};
  background-color: ${({ theme }) => theme.colors.brandNavy100};
  flex: 0;
  display: none;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.desktop} {
    display: flex;
  }
`;

const NavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.white50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }
`;

const AdminNavHeader = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  width: 100%;
`;

const AdminNavDivider = styled.div`
  flex-shrink: 0;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.white10};
`;

const AdminNavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-top: 8px;

  @media ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.white50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }
`;

const AdminNavButton = styled.button<{ $active?: boolean }>`
  border: none;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.colors.white : theme.colors.white75)};
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 500 : 'normal')};
  line-height: 16px;
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 14px;
  width: 100%;
  text-align: left;
  ${({ $active, theme }) =>
    $active &&
    css`
      background-color: ${theme.colors.blue75};
    `}

  & > svg,
  & > div {
    margin-right: 10px;
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  }
`;
